<script setup lang="ts">
  import { ref, useSlots } from 'vue';
  import type { RouteLocationRaw } from 'vue-router';

  import Expand from '../Expand/Expand.vue';
  import type { IconName } from '../Icon/Icon.types';
  import Icon from '../Icon/Icon.vue';

  export interface AppNavigationItemProps {
    /**
     * Location to navigate to
     */
    to: RouteLocationRaw;
    /**
     * Icon to render on the left side
     */
    icon: IconName;
    /**
     * Text label to render
     */
    label: string;
    /**
     * Indicates whether the route is active or not
     */
    isActive?: boolean;
  }

  const isExpanded = ref(false);

  const props = withDefaults(defineProps<AppNavigationItemProps>(), {
    isActive: false,
  });

  const slots = useSlots();
</script>

<template>
  <li>
    <router-link
      v-if="!slots['sub-items']"
      :to="props.to"
      :class="[$style.item, { [$style.active]: props.isActive }]"
      :active-class="$style.active"
      class="flex w-full items-center justify-between font-normal capitalize"
    >
      <div class="flex flex-1 items-center overflow-hidden">
        <Icon :name="props.icon" class="mr-3" />
        <span class="truncate">{{ props.label }}</span>
      </div>
      <!-- @slot Default component slot, for content to the right side of item -->
      <slot></slot>
    </router-link>
    <div v-else>
      <button
        :class="[$style.item, { [$style.expanded]: isExpanded }]"
        class="flex w-full items-center justify-between font-normal capitalize"
        @click="() => (isExpanded = !isExpanded)"
      >
        <div class="flex flex-1 items-center overflow-hidden">
          <Icon :name="props.icon" class="mr-3" />
          <span class="truncate">{{ props.label }}</span>
        </div>
        <Icon :class="[$style.caret, { [$style.expandedIcon]: isExpanded }]" name="chevron-left" />
      </button>
      <Expand :is-expanded="isExpanded" :class="$style.expand">
        <ul :class="$style['sub-items']">
          <!-- @slot Sub items component slot, for rendering nested navigation items -->
          <slot name="sub-items"></slot>
        </ul>
      </Expand>
    </div>
  </li>
</template>

<style module>
  .item {
    border-left: 6px solid transparent;
    color: var(--color-ice-500);
    padding: 10px 12px;
    transition: background-color 150ms;
  }

  .item:hover,
  .item:focus {
    text-decoration: none;
    background: #140e35;
  }

  .sub-items .item {
    padding-left: 40px;
  }

  .expanded {
    background-color: #263256;
  }

  .active {
    color: var(--color-seafoam-500);
    border-color: var(--color-seafoam-500);
    background: #140e35;
  }

  .caret {
    transition: transform 300ms ease-in-out;
  }

  .expanded .caret {
    transform: rotate(-90deg);
  }
</style>
